import type { ReactNode } from 'react';
import type { DeploymentAttempt, DeploymentOutcome, InfrastructureHealth, Member, ServingRelation, SiteState } from '../../api';
import { statusFor } from '../../labels';
import { StatusBadge } from '../ui';
import { Icon } from '../icons';
import { Tooltip, TooltipText } from '../primitives/Tooltip';
import { relativeTime } from '../project/projectHealth';
import { attemptShort } from './statusModel';
import { useQuery } from '../../hooks';
import { formatDuration } from '../runTiming';
import { queries } from '../../queries';

/**
 * The shared pieces every history and summary uses (CO-150): the site's
 * word, a deployment's outcome and serving relation, a time with its exact
 * value on demand, a duration, and who started something. One rendering
 * each, so the same fact never looks two ways on two pages.
 */

/** The live site's word; "checks late" is said, not hidden, when the evidence is stale. */
export function SiteBadge({ state, stale = false, testId }: { state: SiteState; stale?: boolean; testId?: string }) {
  return (
    <span className="site-badge" data-testid={testId}>
      <StatusBadge kind="siteState" value={state} />
      {stale && state !== 'unknown' ? <span className="site-badge__stale">checks late</span> : null}
    </span>
  );
}

/** What happened to an attempt: a tinted pill, red only for a failure. */
export function OutcomeBadge({ outcome, testId }: { outcome: DeploymentOutcome; testId?: string }) {
  return <StatusBadge kind="deploymentOutcome" value={outcome} testId={testId} />;
}

/**
 * The words beside an outcome badge in a list (CO-184, OR4): only what the
 * badge does not already say. "Queued" followed by "Waiting to start", or
 * "Deploying" followed by "In progress", said one fact twice; "Failed"
 * followed by "Previous release active" adds the one thing a failure does not
 * tell. Empty when there is nothing to add.
 */
export function attemptNote(attempt: DeploymentAttempt): string {
  if (attempt.outcome === 'queued' || attempt.outcome === 'deploying') return '';
  return attemptShort(attempt);
}

/** Whether an attempt's release serves now: quiet words, a dot only for "Serving now". */
export function ServingLabel({ serving, testId }: { serving: ServingRelation; testId?: string }) {
  return <StatusBadge kind="serving" value={serving} variant="plain" className={`serving serving--${serving}`} testId={testId} />;
}

const exactFormat = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  timeZoneName: 'short',
});

/** The exact date, time and time zone of an instant. */
export function exactTime(iso: string): string {
  const at = new Date(iso);
  return Number.isFinite(at.getTime()) ? exactFormat.format(at) : iso;
}

/** A project's infrastructure in a list: quiet when nothing warns; its word, and the headline when that says more. */
export function InfrastructureSummary({ infra }: { infra: InfrastructureHealth }) {
  if (infra.level === 'ok') return <span className="cell-stack__secondary">Healthy</span>;
  const word = statusFor('infrastructure', infra.level).label;
  return (
    <span className="cell-stack">
      <StatusBadge kind="infrastructure" value={infra.level} />
      {infra.headline && infra.headline !== word ? <span className="cell-stack__secondary">{infra.headline}</span> : null}
    </span>
  );
}

/**
 * "9 min ago", with the exact date, time and zone on hover. It is not a tab
 * stop: a list of fifty rows would add fifty of them. A screen reader reads
 * the exact time as the description of the relative one (CO-184, DS7).
 */
export function Timestamp({ iso, className }: { iso: string | null | undefined; className?: string }) {
  if (!iso) return <span className="muted">Not recorded</span>;
  return (
    <Tooltip content={exactTime(iso)} hoverOnly>
      <time dateTime={iso} className={`tooltip-text${className ? ` ${className}` : ''}`}>
        {relativeTime(iso)}
      </time>
    </Tooltip>
  );
}

/** How long something took, or has been running. */
export function durationBetween(start: string | null | undefined, end: string | null | undefined, now = Date.now()): string {
  if (!start) return '';
  const from = Date.parse(start);
  const to = end ? Date.parse(end) : now;
  return formatDuration(to - from);
}

/** The time column of a history: when (emphasised), then how long (quiet). */
export function WhenCell({
  created,
  started,
  completed,
  running,
  waiting = false,
}: {
  created: string;
  started?: string | null;
  completed?: string | null;
  running: boolean;
  /** Queued: nothing has started, so the time is a wait, not a run. */
  waiting?: boolean;
}) {
  const took = durationBetween(started ?? created, completed ?? null);
  return (
    <span className="when-cell">
      <Timestamp iso={created} className="when-cell__at" />
      {running ? (
        <span className="when-cell__duration">
          {waiting ? 'waiting for' : 'running for'} {took || 'a moment'}
        </span>
      ) : took ? (
        <span className="when-cell__duration">{took}</span>
      ) : null}
    </span>
  );
}

/** "system: backup schedule" → "Scheduled backup". */
function systemActor(value: string): string | null {
  const match = /^system(?::\s*(.*))?$/i.exec(value.trim());
  if (!match) return null;
  const what = (match[1] ?? '').trim();
  if (!what) return 'Commerce Ops';
  return `Commerce Ops (${what})`;
}

/**
 * A person's display name from their profile: "Jane D." when the profile
 * has a first and a last name, the name as given otherwise. A name is never
 * inferred from an email address.
 */
export function shortName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return name.trim();
  const last = parts[parts.length - 1]!;
  return `${parts[0]} ${last.charAt(0).toUpperCase()}.`;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0]!.charAt(0)}${parts[parts.length - 1]!.charAt(0)}` : (parts[0] ?? '').slice(0, 2)).toUpperCase();
}

export interface Identity {
  /** What the row shows. */
  label: string;
  /** Everything known, for the tooltip. */
  full: string;
  initials?: string;
  kind: 'person' | 'system' | 'unknown';
}

/** Resolves who started something from the record and the team's profiles. */
export function identityOf(value: string | null | undefined, members: Member[] = []): Identity {
  const raw = (value ?? '').trim();
  if (!raw) return { label: 'Not recorded', full: 'Who started this was not recorded.', kind: 'unknown' };
  const system = systemActor(raw);
  if (system) return { label: system, full: `Started automatically: ${raw.replace(/^system:\s*/i, '')}`, kind: 'system' };
  const member = members.find((m) => m.email.toLowerCase() === raw.toLowerCase() || m.name === raw);
  if (member?.name) {
    return { label: shortName(member.name), full: `${member.name} (${member.email})`, initials: initialsOf(member.name), kind: 'person' };
  }
  // An email without a profile name is shown as the email: no name is guessed from it.
  return { label: raw, full: raw, kind: 'person' };
}

/** Who started it: an avatar (initials from a profile name, else a neutral one) and a short label. */
export function Initiator({ value, compact = false }: { value: string | null | undefined; compact?: boolean }) {
  const members = useQuery(queries.members());
  const who = identityOf(value, members.data?.members ?? []);
  const avatar: ReactNode =
    who.kind === 'system' ? (
      <Icon name="clock" size={12} />
    ) : who.initials ? (
      who.initials
    ) : (
      <Icon name="user" size={12} />
    );
  return (
    <span className={`initiator${compact ? ' initiator--compact' : ''}`}>
      <span className={`initiator__avatar initiator__avatar--${who.kind}`} aria-hidden="true">
        {avatar}
      </span>
      <TooltipText label={who.full} className="initiator__label">
        {who.label}
      </TooltipText>
    </span>
  );
}
